import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Track scroll position to shrink logo & header when fixed
  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 20);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className={`sticky top-0 z-50 w-full bg-[#0B0D11]/95 backdrop-blur-md border-b border-[#1A1E26] transition-all duration-300 ${
      isScrolled ? "shadow-lg shadow-black/50" : ""
    }`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-300 ${
        isScrolled ? "h-16" : "h-20 sm:h-24"
      }`}>
        {/* Brand Logo */}
        <Link 
          to="/" 
          className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CC3737] rounded-lg transition-opacity hover:opacity-90 py-1"
          aria-label="Prajwal Halwai - Home"
        >
          <img 
            src="/_assets/yellow-cropped.png" 
            alt="Prajwal Halwai" 
            className={`w-auto object-contain transition-all duration-300 ${
              isScrolled ? "h-8 sm:h-9" : "h-10 sm:h-12 lg:h-13"
            }`}
          />
        </Link>

        {/* Desktop Nav - visible at md/lg */}
        <nav className="hidden md:flex items-center gap-1.5" aria-label="Main navigation">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
                isActive
                  ? "text-white bg-[#1A1E27]"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#151820]"
              }`
            }
          >
            Home
          </NavLink>

          <NavLink
            to="/work"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
                isActive
                  ? "text-white bg-[#1A1E27]"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#151820]"
              }`
            }
          >
            Work
          </NavLink>

          <NavLink
            to="/about"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
                isActive
                  ? "text-white bg-[#1A1E27]"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#151820]"
              }`
            }
          >
            About
          </NavLink>

          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
                isActive
                  ? "text-white bg-[#1A1E27]"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#151820]"
              }`
            }
          >
            Contact
          </NavLink>
        </nav>

        {/* CTA Button & Mobile Trigger */}
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="hidden sm:inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#CC3737] hover:bg-[#b52f2f] active:bg-[#9e2727] transition-all shadow-md shadow-[#CC3737]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CC3737]"
          >
            <span>Discuss your idea</span>
            <i className="fa-solid fa-arrow-right text-xs" />
          </Link>

          {/* Burger menu button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2.5 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#1A1E26] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CC3737] transition-colors"
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
          >
            <i className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"} text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Drawer Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0F1218] border-b border-[#202532] px-4 pt-3 pb-6 space-y-2 animate-in slide-in-from-top-2 duration-200">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `block px-3.5 py-2.5 text-base font-medium rounded-lg ${
                isActive
                  ? "text-white bg-[#1C212D]"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#151922]"
              }`
            }
          >
            Home
          </NavLink>

          <NavLink
            to="/work"
            className={({ isActive }) =>
              `block px-3.5 py-2.5 text-base font-medium rounded-lg ${
                isActive
                  ? "text-white bg-[#1C212D]"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#151922]"
              }`
            }
          >
            Work
          </NavLink>

          <NavLink
            to="/about"
            className={({ isActive }) =>
              `block px-3.5 py-2.5 text-base font-medium rounded-lg ${
                isActive
                  ? "text-white bg-[#1C212D]"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#151922]"
              }`
            }
          >
            About
          </NavLink>

          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `block px-3.5 py-2.5 text-base font-medium rounded-lg ${
                isActive
                  ? "text-white bg-[#1C212D]"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#151922]"
              }`
            }
          >
            Contact
          </NavLink>

          <div className="pt-4 mt-4 border-t border-[#1F2533]">
            <Link
              to="/contact"
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold text-white bg-[#CC3737] hover:bg-[#b52f2f] transition-all"
            >
              <span>Discuss your idea</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
